import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { Avatar, AvatarFallbackText, AvatarImage } from '@/components/ui/avatar';
import { FlatList } from '@/components/ui/flat-list';
import { GlassSurface } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { CloseIcon, SearchIcon } from '@/components/ui/icon';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import { searchUsersV2, type SearchUsersV2User } from '@/services/searchUsersV2';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

const PAGE_SIZE = 20;

export const MIN_QUERY_LENGTH = 2;

/** How far from the end of the list the next page starts loading, in list heights. */
const LOAD_MORE_THRESHOLD = 0.5;

type SupportRecipientPickerProps = {
  onSelect: (user: SearchUsersV2User) => void;
  /** The person whose thread is being opened, shown with a spinner; every row is inert meanwhile. */
  pendingUserId?: number;
  /** Clearance for a header that floats over the list. */
  topInset?: number;
};

/**
 * Finds one person to message. Searching starts at two characters and an empty
 * box lists everyone, as the user search has always done; choosing a row is the
 * whole interaction, so rows carry no checkbox and the screen no second step.
 * What staff type goes to the API as typed: it matches every word against the
 * first name, last name and email, so a full name or an email fragment works.
 *
 * The search field floats on glass, like the support thread's composer, and
 * pins under the header while the people scroll beneath it.
 */
export function SupportRecipientPicker({
  onSelect,
  pendingUserId,
  topInset = 0,
}: SupportRecipientPickerProps) {
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();

  const isTooShort = trimmedQuery.length > 0 && trimmedQuery.length < MIN_QUERY_LENGTH;

  const search = useInfiniteQuery({
    queryKey: ['searchUsersV2', 'supportRecipient', trimmedQuery, PAGE_SIZE],
    queryFn: ({ pageParam }) =>
      searchUsersV2({ q: trimmedQuery, page: pageParam, limit: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage && lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
    enabled: !isTooShort,
  });

  const users = search.data?.pages.flatMap((page) => page?.users ?? []) ?? [];

  const loadMore = () => {
    if (search.hasNextPage && !search.isFetchingNextPage) {
      search.fetchNextPage().catch(() => undefined);
    }
  };

  return (
    <VStack className="w-full flex-1" style={{ paddingTop: topInset }}>
      <View className="mx-auto w-full max-w-3xl px-4 pb-2 pt-2">
        <RecipientSearchField value={query} onChangeText={setQuery} />
      </View>

      <FlatList
        data={users}
        keyExtractor={(user) => String(user.id)}
        renderItem={({ item }) => (
          <RecipientRow
            user={item}
            isPending={item.id === pendingUserId}
            isDisabled={pendingUserId !== undefined}
            onSelect={onSelect}
          />
        )}
        onEndReached={loadMore}
        onEndReachedThreshold={LOAD_MORE_THRESHOLD}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        className="flex-1"
        contentContainerClassName="mx-auto w-full max-w-3xl pb-6"
        ListEmptyComponent={
          <RecipientListEmpty
            isTooShort={isTooShort}
            isLoading={search.isLoading}
            isError={search.isError}
            hasQuery={trimmedQuery.length > 0}
          />
        }
        ListFooterComponent={
          search.isFetchingNextPage ? (
            <HStack className="items-center justify-center py-3">
              <Spinner />
            </HStack>
          ) : null
        }
      />
    </VStack>
  );
}

function RecipientSearchField({
  value,
  onChangeText,
}: {
  value: string;
  onChangeText: (text: string) => void;
}) {
  const { colors } = useColors();

  return (
    <GlassSurface
      className="w-full rounded-full"
      fallbackClassName="border border-outline-500 bg-background-0">
      <Input size="lg" className="h-12 w-full border-0 bg-transparent">
        <InputSlot className="h-full shrink-0 pl-4 pr-0">
          <InputIcon as={SearchIcon} size="lg" className="text-typography-700" />
        </InputSlot>
        <InputField
          value={value}
          onChangeText={onChangeText}
          placeholder="Search by name"
          aria-label="Search people"
          accessibilityLabel="Search people"
          autoFocus
          autoCorrect={false}
          autoCapitalize="words"
          returnKeyType="search"
          className="pl-2 pr-4 text-base placeholder:text-typography-600"
          style={{ color: colors.typography[900] }}
        />
        {value.length > 0 ? (
          <InputSlot
            className="flex min-h-[48px] min-w-[48px] items-center justify-center pr-2"
            {...getAccessibilityProps({ label: 'Clear search', role: 'button' })}
            onPress={() => onChangeText('')}>
            <InputIcon as={CloseIcon} size="lg" className="text-typography-700" />
          </InputSlot>
        ) : null}
      </Input>
    </GlassSurface>
  );
}

function RecipientRow({
  user,
  isPending,
  isDisabled,
  onSelect,
}: {
  user: SearchUsersV2User;
  isPending: boolean;
  isDisabled: boolean;
  onSelect: (user: SearchUsersV2User) => void;
}) {
  const { name, initials } = getProfileNameAndInitials(user);

  const displayName = name || user.email || 'Unknown';

  const avatarSource = getAvatarUrl(user.avatar ?? undefined, true);

  return (
    <Pressable
      onPress={() => onSelect(user)}
      disabled={isDisabled}
      {...getAccessibilityProps({ label: `Message ${displayName}`, role: 'button' })}
      accessibilityState={{ disabled: isDisabled, busy: isPending }}
      className={twJoin(
        'min-h-[64px] flex-row items-center gap-3 px-4 py-2 web:cursor-pointer',
        'data-[hover=true]:bg-background-100 data-[active=true]:bg-background-100'
      )}>
      <Avatar size="md">
        <AvatarFallbackText>{initials || displayName}</AvatarFallbackText>
        {avatarSource ? <AvatarImage source={avatarSource} /> : null}
      </Avatar>

      <VStack className="min-w-0 flex-1">
        <Text className="text-base text-typography-900" numberOfLines={1}>
          {displayName}
        </Text>
        {user.email && name ? (
          <Text className="text-sm text-typography-700" numberOfLines={1}>
            {user.email}
          </Text>
        ) : null}
      </VStack>

      {isPending ? <Spinner size="small" accessibilityLabel="Opening conversation" /> : null}
    </Pressable>
  );
}

function RecipientListEmpty({
  isTooShort,
  isLoading,
  isError,
  hasQuery,
}: {
  isTooShort: boolean;
  isLoading: boolean;
  isError: boolean;
  hasQuery: boolean;
}) {
  if (isLoading) {
    return (
      <HStack className="items-center justify-center py-8">
        <Spinner />
      </HStack>
    );
  }

  const message = emptyListMessage({ isTooShort, isError, hasQuery });

  if (!message) {
    return null;
  }

  return (
    <HStack className="items-center justify-center px-6 py-8">
      <Text className="text-center text-sm text-typography-700" accessibilityLiveRegion="polite">
        {message}
      </Text>
    </HStack>
  );
}

function emptyListMessage({
  isTooShort,
  isError,
  hasQuery,
}: {
  isTooShort: boolean;
  isError: boolean;
  hasQuery: boolean;
}): string | null {
  if (isTooShort) {
    return `Type at least ${MIN_QUERY_LENGTH} characters to search`;
  }

  if (isError) {
    return 'Could not search people. Try again.';
  }

  return hasQuery ? 'No people found' : null;
}
